/**
 * Auth API 타입 및 Hooks
 * @MP/shared/api/hooks/auth에서 타입과 hooks를 모두 import할 수 있도록 함
 */

// 타입 re-export (실제 사용되는 타입만 명시적으로 export)
// 1.4 Model
// Organization type was unused outside this module; remove export to keep barrel surface tight
export type { OrganizationResponseDto as OrganizationResponse } from '../generated/auth/1.4/model/organization-response-dto';
export type { OrganizationRequestDto as OrganizationRequest } from '../generated/auth/1.4/model/organization-request-dto';
export type { OrganizationSummaryDto as OrganizationSummary } from '../generated/auth/1.4/model/organization-summary-dto';
export type { EmployeeResponseDto as EmployeeResponse } from '../generated/auth/1.4/model/employee-response-dto';
export { EmployeeResponseDtoRoleEnum as EmployeeRole } from '../generated/auth/1.4/model/employee-response-dto';

// public1.0 Model
export type { CreateOrganizationDto as CreateOrganization } from '../generated/auth/public1.0/model/create-organization-dto';
export type { CreateEmployeeDto as CreateEmployee } from '../generated/auth/public1.0/model/create-employee-dto';
export type { DepartmentDto as Department } from '../generated/auth/public1.0/model/department-dto';

// Hooks 구현
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosHeaders } from 'axios';

import { API_CONFIG } from '../config/env';
import {
  Configuration,
  EmployeeAgreementUpdateDto,
  EmployeeControllerApi,
  OrganizationControllerApi,
  OrganizationRequestDto as OrganizationRequest,
  UpdatePasswordDto,
  UploadBusinessLicenseFileRequest,
} from '../generated/auth/1.4';
import {
  CreateEmployeeDto as CreateEmployee,
  EmployeeResponseDto as EmployeeResponse,
  PublicControllerApi,
} from '../generated/auth/public1.0';
import {
  QK_AUTH_CURRENT_USER,
  QK_AUTH_DEPARTMENT_LIST,
  QK_AUTH_EMPLOYEE_DETAIL,
  QK_AUTH_MEMBER_LIST,
  QK_AUTH_ORGANIZATION_LIST,
} from '../query-keys';

const config = new Configuration({ basePath: API_CONFIG.AUTH_URL });
const employee = new EmployeeControllerApi(config);
const authPublic = new PublicControllerApi(config);
const organization = new OrganizationControllerApi(config);

export const useLogin = () => {
  return useMutation<string, AxiosError, { email: string; password: string }>({
    mutationFn: ({ email, password }) =>
      employee
        .createOnlyToken(
          {
            username: email,
            password: password,
            clientId: 'mindd-hospital-web',
            clientSecret: API_CONFIG.AUTH_CLIENT_SECRET,
          },
          {
            headers: {
              common: new AxiosHeaders(),
            },
          }
        )
        .then((res) => res.data),
  });
};

export const useFetchOrganizationSummaryByName = (name?: string) => {
  return useQuery({
    queryKey: [QK_AUTH_ORGANIZATION_LIST, name],
    queryFn: () => authPublic.getOrganizationsByNameLike(name!).then((res) => res.data),
    enabled: !!name && name.trim().length >= 3,
  });
};

const useFetchOrganizationById = (orgId?: number) => {
  return useQuery({
    queryKey: [QK_AUTH_ORGANIZATION_LIST, orgId],
    queryFn: () => organization.getOrganizationBy(orgId!).then((res) => res.data),
    enabled: !!orgId,
  });
};

export const useFetchCurrentUser = () => {
  return useQuery({
    queryKey: [QK_AUTH_CURRENT_USER],
    queryFn: () => employee.getEmployeeMe().then((res) => res.data),
  });
};

export const useFetchEmployeeById = (employeeId?: number) => {
  return useQuery({
    queryKey: [QK_AUTH_EMPLOYEE_DETAIL, employeeId],
    queryFn: () => employee.getEmployeeBy(employeeId!).then((res) => res.data),
    enabled: !!employeeId,
  });
};

/** lazy fetch (다운로드 등 클릭 시점 호출용) */
export const fetchEmployeeById = (client: QueryClient, employeeId: number) => {
  return client.fetchQuery({
    queryKey: [QK_AUTH_EMPLOYEE_DETAIL, employeeId],
    queryFn: () => employee.getEmployeeBy(employeeId).then((res) => res.data),
  });
};

export const useFetchOrganizationOfCurrentUser = () => {
  const { data: currentUser } = useFetchCurrentUser();
  return useFetchOrganizationById(currentUser?.orgzId);
};

export const useOrganizationMembersOfCurrentUser = () => {
  const { data: currentUser } = useFetchCurrentUser();
  return useQuery({
    queryKey: [QK_AUTH_MEMBER_LIST, currentUser?.orgzId],
    queryFn: () => organization.getUsers(currentUser?.orgzId!).then((res) => res.data),
    enabled: !!currentUser && !!currentUser.orgzId,
  });
};

export const useCheckEmailExists = () => {
  return useMutation<boolean, AxiosError, { email: string }>({
    mutationFn: ({ email }) => authPublic.checkEmailExists(email).then((res) => res.data),
  });
};

export const useValidateEmployee = () => {
  return useMutation<boolean, AxiosError, { email: string; name: string; tel: string }>({
    mutationFn: ({ email, name, tel }) => authPublic.validateEmployee(email, name, tel).then((res) => res.data),
  });
};

export const useSendCertNumber = () => {
  return useMutation<number, AxiosError, { phone: string }>({
    mutationFn: ({ phone }) => authPublic.sendSmsVerificationCode(phone).then((res) => res.data),
  });
};

export const useVerifyCertNumber = () => {
  return useMutation<boolean, AxiosError, { certId: number; certNumber: string }>({
    mutationFn: ({ certId, certNumber }) => authPublic.verifySmsCode(certId, certNumber).then((res) => res.data),
  });
};

export const useCreateEmployee = () => {
  return useMutation<EmployeeResponse, AxiosError, { certId: number; certNumber: string; employee: CreateEmployee }>({
    mutationFn: ({ certId, certNumber, employee }) =>
      authPublic.createEmployee(certId, certNumber, 'mindd-hospital-web', employee).then((res) => res.data),
  });
};

export const useUploadBLFile = () => {
  const client = useQueryClient();
  return useMutation<string, AxiosError, { id: number; blFile: File }>({
    mutationFn: ({ id, blFile }) => {
      const data = new FormData();
      data.append('file', blFile);
      const fileData = data as unknown;
      return organization
        .uploadBusinessLicenseFile(id, fileData as UploadBusinessLicenseFileRequest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res) => res.data);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QK_AUTH_ORGANIZATION_LIST] });
    },
  });
};

export const useUpdateOrganization = () => {
  const { data: currentUser } = useFetchCurrentUser();
  const client = useQueryClient();

  return useMutation<any, any, OrganizationRequest>({
    mutationFn: (body) => {
      if (currentUser?.orgzId) {
        return organization.updateOrganization(currentUser?.orgzId, body);
      }
      return Promise.resolve();
    },
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: [QK_AUTH_ORGANIZATION_LIST, currentUser?.orgzId],
      }),
  });
};

export const useFetchDepartments = (hpid?: string) => {
  return useQuery({
    queryKey: [QK_AUTH_DEPARTMENT_LIST, hpid],
    queryFn: () => authPublic.getDepartments(hpid!).then((res) => res.data),
    enabled: !!hpid,
    gcTime: 0,
  });
};

export const useUpdatePassword = () => {
  return useMutation<any, AxiosError<{ errorCode: string }>, UpdatePasswordDto>({
    mutationFn: (model) => employee.updatePassword(model),
  });
};

export const useResetPassword = () => {
  return useMutation<any, AxiosError, { email: string; id: number; code: string; password: string }>({
    mutationFn: ({ email, id, code, password }) =>
      authPublic.patchEmployeePassword(email, id, code, { password: password }),
  });
};

export const useApproveMember = () => {
  const client = useQueryClient();
  return useMutation<any, AxiosError, { memberId: number }>({
    mutationFn: ({ memberId }) => employee.updateRoles(memberId, 'U_NORMAL'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QK_AUTH_MEMBER_LIST] });
    },
  });
};

export const useDelegateAdmin = () => {
  const client = useQueryClient();
  return useMutation<any, AxiosError, { memberId: number }>({
    mutationFn: ({ memberId }) => employee.updateRoles(memberId, 'U_ADMIN'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QK_AUTH_MEMBER_LIST] });
      client.invalidateQueries({ queryKey: [QK_AUTH_CURRENT_USER] });
    },
  });
};

export const useIsOrganizationUsingStim = () => {
  const query = useFetchOrganizationOfCurrentUser();
  return {
    ...query,
    data: query.data ? (query.data.services! & 512) === 512 : undefined,
  };
};

export const useUpdateAgreement = () => {
  const client = useQueryClient();
  return useMutation<any, AxiosError, { userId: number; update: EmployeeAgreementUpdateDto }>({
    mutationFn: ({ userId, update }) => employee.updateAgreement(userId, update),
    onSuccess: () => client.invalidateQueries({ queryKey: [QK_AUTH_CURRENT_USER] }),
  });
};
